import { WT_TAG_CATEGORIES } from "@/lib/winetech_tags";

/**
 * Multi-tag picker grouped by the 7 WineTech categories.
 *
 * Props:
 *  - values: { tag_maturity: string[], tag_value_chain: string[], ... }
 *  - onChange: (key, next_list) => void
 *  - compact: boolean — render denser variant (for modals)
 *  - testId: string — base test id
 */
export default function TagCategorySelector({ values, onChange, compact = false, testId = "tag-selector" }) {
  return (
    <div className={compact ? "space-y-4" : "space-y-6"} data-testid={testId}>
      {WT_TAG_CATEGORIES.map((cat) => {
        const selected = values?.[`tag_${cat.key}`] || [];
        return (
          <div key={cat.key} data-testid={`${testId}-cat-${cat.key}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-600">{cat.label}</div>
              {selected.length > 0 && (
                <span className="text-[10px] font-bold text-wine-600">{selected.length} sélectionné{selected.length > 1 ? "s" : ""}</span>
              )}
            </div>
            <div className={`mt-2 flex flex-wrap ${compact ? "gap-1" : "gap-1.5"}`}>
              {cat.options.map((opt) => {
                const isSel = selected.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      const next = isSel ? selected.filter((x) => x !== opt) : [...selected, opt];
                      onChange(`tag_${cat.key}`, next);
                    }}
                    className={`rounded-full border ${compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"} font-medium transition ${
                      isSel
                        ? `${cat.badge} font-bold ring-1 ring-current`
                        : "border-neutral-200 bg-white text-neutral-600 hover:border-wine-300 hover:bg-wine-50/40"
                    }`}
                    data-testid={`${testId}-${cat.key}-${opt}`}
                    aria-pressed={isSel}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Read-only display of all selected tags grouped by category.
 * Only renders categories with at least one selected tag.
 */
export function TagCategoryDisplay({ values, compact = false, testId = "tag-display" }) {
  const groups = WT_TAG_CATEGORIES
    .map((c) => ({ ...c, selected: (values?.[`tag_${c.key}`] || []) }))
    .filter((c) => c.selected.length > 0);
  if (groups.length === 0) return null;
  return (
    <div className={compact ? "space-y-2" : "space-y-3"} data-testid={testId}>
      {groups.map((cat) => (
        <div key={cat.key} data-testid={`${testId}-cat-${cat.key}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{cat.label}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {cat.selected.map((opt) => (
              <span key={opt} className={`rounded-full border ${cat.badge} px-2.5 py-0.5 text-[11px] font-semibold`}>{opt}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
